import { AuthFailureCode, fetcher } from '@commenda/utils';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

// Must match BE TotpService digit count (RFC 6238 §5.3).
export const OTP_LENGTH = 6;

const setupTwoFactorResponseSchema = z.object({
  otpauthUrl: z.string().url(),
  qrCodeDataUrl: z.string().startsWith('data:image/'),
});
export type SetupTwoFactorResponse = z.infer<typeof setupTwoFactorResponseSchema>;

export type SetupTwoFactorResult =
  | { type: 'ready'; data: SetupTwoFactorResponse }
  | { type: 'already-enrolled' }
  | { type: 'invalid-response' }
  | { type: 'unauthorized' }
  | { type: 'error' };

async function setupTwoFactorRequest(): Promise<SetupTwoFactorResult> {
  const res = await fetcher('auth/setup-2fa', { method: 'POST' });
  if (!res.ok) {
    // First-factor session gone (uncoded 401/403) — bounce to signin, don't strand the setup page.
    if (res.status === 401 || res.status === 403) return { type: 'unauthorized' };
    // Match by code, not 409 — other 409 reasons must not become 'already-enrolled'.
    const body = (await res.json().catch(() => null)) as { code?: string } | null;
    if (body?.code === AuthFailureCode.ENROLLMENT_CONFLICT) return { type: 'already-enrolled' };
    return { type: 'error' };
  }
  const parsed = setupTwoFactorResponseSchema.safeParse(await res.json());
  return parsed.success
    ? { type: 'ready', data: parsed.data }
    : { type: 'invalid-response' };
}

export function useSetupTwoFactor() {
  return useMutation({ mutationFn: setupTwoFactorRequest });
}

export type VerifyTwoFactorVars = {
  otp: string;
  redirectTo: string;
  rememberDevice?: boolean;
};

export type VerifyTwoFactorResult =
  | { type: 'success' }
  | { type: 'unauthorized' }
  | { type: 'error'; code: string | null };

// `redirect: 'manual'` keeps the BE's cross-origin 301 as a typed `opaqueredirect`
// response — without it the browser silently follows and we can't observe success.
async function verifyTwoFactorRequest({
  otp,
  redirectTo,
  rememberDevice,
}: VerifyTwoFactorVars): Promise<VerifyTwoFactorResult> {
  const response = await fetcher('auth/verify-otp', {
    method: 'POST',
    body: { otp, redirectTo, rememberDevice },
    redirect: 'manual',
  });
  if (response.type === 'opaqueredirect') return { type: 'success' };
  if (response.status === 401 || response.status === 403) return { type: 'unauthorized' };
  const body = (await response.json().catch(() => null)) as { code?: string } | null;
  return { type: 'error', code: body?.code ?? null };
}

export function useVerifyTwoFactor() {
  return useMutation({ mutationFn: verifyTwoFactorRequest });
}
